/* eslint-disable @typescript-eslint/no-explicit-any */
/** Admin: user approvals, roles & permissions matrix, pricing library. Ported from `admin-auth.jsx`. */
import { useEffect, useState } from 'react';
import React from 'react';
import { ROLE_DEFS, CAPS, DM_CATALOG, dmItemCost, VAR_GROUPS } from '@heros/shared';
import type { Role, User as UserDTO, PendingUser, Project, PasswordResetRequest, AnalysisFormula } from '@heros/shared';
import { useData, useDataActions } from '../context/AppDataContext';
import { useCan, useAuth } from '../context/AuthContext';
import { useProfileViewer } from '../components/ProfileViewer';
import { apiClient, ApiClientError } from '../lib/apiClient';
import { downloadCsv, dateStamp, copyText } from '../lib/exportUtils';
import { Ico, Avatar, Modal, useToast, RowMenu } from '../components/ui';

/** Online/offline presence from a lastSeenAt ISO timestamp (online = seen < 5 min ago).
 *  `ago` is a full days+hours+mins breakdown (e.g. "2d 3h 15m ago") so the exact
 *  last-online elapsed time is visible; `exact` is the absolute local timestamp (tooltip). */
const PRESENCE_ONLINE_MS = 5 * 60 * 1000;
function userPresence(lastSeenAt?: string): { online: boolean; ago: string; exact: string } {
  if (!lastSeenAt) return { online: false, ago: 'never signed in', exact: 'No recorded sign-in' };
  const t = new Date(lastSeenAt).getTime();
  if (Number.isNaN(t)) return { online: false, ago: '—', exact: '' };
  const d = Date.now() - t;
  const exact = new Date(t).toLocaleString();
  if (d < PRESENCE_ONLINE_MS) return { online: true, ago: 'now', exact };
  const totalMin = Math.floor(d / 60000);
  const days = Math.floor(totalMin / 1440);
  const hours = Math.floor((totalMin % 1440) / 60);
  const mins = totalMin % 60;
  const parts: string[] = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  parts.push(`${mins}m`); // always show minutes so it's never just "2d"
  return { online: false, ago: `${parts.join(' ')} ago`, exact };
}

/** Best-effort map from a free-text/requested role label to a canonical RBAC roleKey. */
function roleKeyFromLabel(label: string): Role {
  const r = (label || '').toLowerCase();
  if (r.includes('super')) return 'super';
  if (r.includes('admin')) return 'admin';
  if (r.includes('statistician') || r.includes('stat')) return 'stat';
  if (r.includes('data manager')) return 'dm';
  if (r.includes('research assistant')) return 'ra';
  if (r.includes('monitor')) return 'monitor';
  if (r.includes('finance')) return 'finance';
  if (r.includes('viewer')) return 'viewer';
  if (r.includes('principal investigator') || r === 'pi') return 'pi';
  if (r.includes('project manager') || r.includes('pm')) return 'pm';
  return 'pm';
}

// AdminUsers is self-sufficient against the API. The props (kept for App-shell
// compatibility) are no longer used to source data or mutate; everything below
// reads `apiClient.users.*` directly so admin actions hit the real backend.
export function AdminUsers(_props?: { pending?: any[]; approveUser?: (id: string) => void; rejectUser?: (id: string) => void }) {
  void _props;
  const D = useData();
  const [tab, setTab] = useState('active');
  const [open, setOpen] = useState<any>(null);
  const [openUser, setOpenUser] = useState<any>(null);
  const { open: openProfile } = useProfileViewer();
  const toast = useToast();

  const [users, setUsers] = useState<UserDTO[]>([]);
  const [pending, setPending] = useState<PendingUser[]>([]);
  const [resets, setResets] = useState<PasswordResetRequest[]>([]);
  // Temp password issued by a reset action — shown ONCE in TempPasswordModal.
  const [tempPw, setTempPw] = useState<{ password: string; name: string } | null>(null);
  const [activeSort, setActiveSort] = useState<'recent' | 'az' | 'role'>('recent');
  const [pendingSort, setPendingSort] = useState<'recent' | 'az'>('recent');
  // Projects are fetched fresh here (not just read from context) so the
  // per-user "Projects" count always reflects PRESENT membership — context can
  // lag a server-side change (e.g. a member auto-linked/removed elsewhere).
  // Seeded from context for an instant first paint, then refreshed by loadData.
  const [projects, setProjects] = useState<Project[]>(D.projects);
  const [forbidden, setForbidden] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [roleEditUser, setRoleEditUser] = useState<any>(null);
  const canManageUsers = useCan('sys.users.manage') === 'allow';
  const { currentUser } = useAuth();
  const isSuper = (currentUser as any)?.roleKey === 'super';

  const loadData = React.useCallback(async () => {
    setLoadingData(true);
    try {
      const [u, p, rr, projs] = await Promise.all([
        apiClient.users.list('all'),
        apiClient.users.pending(),
        apiClient.users.passwordResets.list('pending').catch(() => [] as PasswordResetRequest[]),
        apiClient.projects.list().catch(() => null),
      ]);
      setUsers(u);
      setPending(p);
      setResets(rr);
      if (projs) setProjects(projs);
      setForbidden(false);
    } catch (err) {
      if (err instanceof ApiClientError && err.isForbidden) {
        setForbidden(true);
      } else {
        toast(err instanceof ApiClientError ? (err.body?.message ?? 'Failed to load users.') : 'Failed to load users.');
      }
    } finally {
      setLoadingData(false);
    }
  }, [toast]);

  useEffect(() => { void loadData(); }, [loadData]);

  const doApprove = async (id: string, roleKey: Role, role: string, team: string) => {
    setBusyId(id);
    try {
      const created = await apiClient.users.approve(id, { roleKey, role, team });
      toast(`Approved ${created.name}`);
      setOpen(null);
      await loadData();
    } catch (err) {
      toast(err instanceof ApiClientError ? (err.body?.message ?? 'Approval failed.') : 'Approval failed.');
    } finally {
      setBusyId(null);
    }
  };

  const doSetRole = async (id: string, roleKey: Role, role: string) => {
    setBusyId(id);
    try {
      const updated = await apiClient.users.setRole(id, { roleKey, role });
      toast(`${updated.name} is now ${updated.role}`);
      setRoleEditUser(null);
      await loadData();
    } catch (err) {
      toast(err instanceof ApiClientError ? (err.body?.message ?? 'Could not change role.') : 'Could not change role.');
    } finally {
      setBusyId(null);
    }
  };

  const doReject = async (id: string, name?: string) => {
    setBusyId(id);
    try {
      await apiClient.users.reject(id);
      toast(`Rejected ${name ?? 'applicant'}`);
      setOpen(null);
      await loadData();
    } catch (err) {
      toast(err instanceof ApiClientError ? (err.body?.message ?? 'Reject failed.') : 'Reject failed.');
    } finally {
      setBusyId(null);
    }
  };

  /** Suspend / reactivate a user (used from the Suspended tab and the detail modal). */
  const doSetStatus = async (u: any, status: 'Active' | 'Suspended') => {
    setBusyId(u.id);
    try {
      const updated = await apiClient.users.setStatus(u.id, status);
      toast(`${updated.name} ${status === 'Suspended' ? 'suspended' : 'reactivated'}`);
      setOpenUser(null);
      await loadData();
    } catch (err) {
      toast(err instanceof ApiClientError ? (err.body?.message ?? 'Could not update status.') : 'Could not update status.');
    } finally {
      setBusyId(null);
    }
  };

  /** Issue a temporary password (self is blocked; confirm before hitting the server).
   *  `requestId` resolves the originating reset-request row when called from that queue. */
  const doResetPassword = async (u: any, requestId?: string) => {
    if (u.id === currentUser?.id) return;
    if (!window.confirm(`Issue a temporary password for ${u.name}? Their current password will stop working immediately.`)) return;
    setBusyId(requestId ?? u.id);
    try {
      const res = await apiClient.users.resetPassword(u.id, requestId);
      setTempPw({ password: res.tempPassword, name: res.user.name });
      setOpenUser(null);
      await loadData();
    } catch (err) {
      toast(err instanceof ApiClientError ? (err.body?.message ?? 'Reset failed.') : 'Reset failed.');
    } finally {
      setBusyId(null);
    }
  };

  /** Dismiss a password-reset request (no password issued). */
  const doDismissReset = async (id: string) => {
    setBusyId(id);
    try {
      await apiClient.users.passwordResets.dismiss(id);
      toast('Reset request dismissed');
      await loadData();
    } catch (err) {
      toast(err instanceof ApiClientError ? (err.body?.message ?? 'Dismiss failed.') : 'Dismiss failed.');
    } finally {
      setBusyId(null);
    }
  };

  // Derived rosters (server returns ALL statuses; split client-side).
  const activeUsers = users.filter((u) => (u.status ?? 'Active') !== 'Suspended');
  const suspendedUsers = users.filter((u) => u.status === 'Suspended');
  const fmtLastLogin = (iso?: string) => (iso ? new Date(iso).toLocaleString() : '—');

  const sortedActive = [...activeUsers].sort((a, b) => {
    if (activeSort === 'az') return a.name.localeCompare(b.name);
    if (activeSort === 'role') return a.role.localeCompare(b.role) || a.name.localeCompare(b.name);
    const ta = a.lastSeenAt ? new Date(a.lastSeenAt).getTime() : -Infinity;
    const tb = b.lastSeenAt ? new Date(b.lastSeenAt).getTime() : -Infinity;
    return tb - ta; // most-recent first, missing last
  });
  const sortedPending = [...pending].sort((a, b) =>
    pendingSort === 'az' ? a.name.localeCompare(b.name) : String(b.submitted).localeCompare(String(a.submitted)),
  );

  if (forbidden) {
    return (
      <div className="page-pad fadein">
        <div className="page-head"><div><h2>Users &amp; Permissions</h2></div></div>
        <div className="card"><div className="card-b" style={{ padding: 40, textAlign: 'center', color: 'var(--text-2)' }}>
          <div style={{ fontSize: 15, marginBottom: 4 }}>🔒 You don't have permission to manage users.</div>
          <div style={{ fontSize: 12.5 }}>This area requires the Super Admin or Admin role.</div>
        </div></div>
      </div>
    );
  }

  const tabs = [
    { id: 'active', label: 'Active', count: activeUsers.length },
    { id: 'pending', label: 'Pending approval', count: pending.length },
    { id: 'suspended', label: 'Suspended', count: suspendedUsers.length },
    { id: 'resets', label: 'Reset requests', count: resets.length },
    { id: 'roles', label: 'Roles & Permissions', count: null as number | null },
  ];

  return (
    <div className="page-pad fadein">
      <div className="page-head">
        <div>
          <h2>Users &amp; Permissions</h2>
          <div className="sub">{activeUsers.length} active · {pending.length} pending approval · {ROLE_DEFS.length} roles defined</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={() => void loadData()} disabled={loadingData}><Ico.dl /> Refresh</button>
          <button
            className="btn primary"
            title="Copy the self-registration link to share. New sign-ups appear here as pending approvals."
            onClick={() => {
              const link = `${window.location.origin}/?register=1`;
              const done = () => toast('Registration link copied — share it. New users appear here as pending approvals.');
              if (navigator.clipboard?.writeText) {
                navigator.clipboard.writeText(link).then(done).catch(() => window.prompt('Copy this registration link:', link));
              } else {
                window.prompt('Copy this registration link:', link);
              }
            }}
          ><Ico.plus /> Invite (copy link)</button>
        </div>
      </div>

      <div className="tabs">
        {tabs.map((t) => (
          <button key={t.id} className={'tab' + (tab === t.id ? ' active' : '')} onClick={() => setTab(t.id)}>
            {t.label}{t.count != null ? ` (${t.count})` : ''}
          </button>
        ))}
      </div>

      {tab === 'active' && (
        <div className="card">
          <div className="card-h">
            <h3>Active team members</h3>
            <div style={{ display: 'flex', gap: 6 }}>
              <select value={activeSort} onChange={(e) => setActiveSort(e.target.value as 'recent' | 'az' | 'role')} style={{ width: 'auto', height: 28 }}>
                <option value="recent">Sort: most recently active</option>
                <option value="az">Sort: A–Z</option>
                <option value="role">Sort: by role</option>
              </select>
            </div>
          </div>
          <div className="card-b flush">
            {loadingData ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-2)', fontSize: 12.5 }}>Loading users…</div>
            ) : sortedActive.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-2)', fontSize: 12.5 }}>No active users.</div>
            ) : (
            <table>
              <thead><tr><th>Member</th><th>Role</th><th>Email</th><th>Projects</th><th>Last login</th><th>Presence</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {sortedActive.map((u) => {
                  const projCount = projects.filter((p) => p.members.includes(u.id)).length;
                  return (
                    <tr key={u.id} className="hover-row" onClick={() => setOpenUser(u)}>
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <Avatar user={u as any} />
                          <div>
                            <div style={{ fontWeight: 500 }}>{u.name}</div>
                            <div style={{ fontSize: 10.5, color: 'var(--text-2)' }} className="mono">{u.email}</div>
                          </div>
                        </span>
                      </td>
                      <td><span className="chip" data-tone="violet"><span className="dot" />{u.role}</span></td>
                      <td className="mono" style={{ fontSize: 11.5, color: 'var(--text-2)' }}>{u.email}</td>
                      <td className="tabular mono">{projCount}</td>
                      <td className="mono" style={{ fontSize: 11.5, color: 'var(--text-2)' }}>{fmtLastLogin(u.lastLogin)}</td>
                      <td>{(() => { const p = userPresence((u as any).lastSeenAt); return <span className="chip" data-tone={p.online ? 'green' : 'slate'} title={p.exact ? `Last seen: ${p.exact}` : undefined}><span className="dot" style={p.online ? { background: '#4a8a55' } : { background: 'var(--text-3)' }} />{p.online ? 'Online now' : `Last seen ${p.ago}`}</span>; })()}</td>
                      <td><span className="chip" data-tone="green"><span className="dot" />{u.status}</span></td>
                      <td><RowMenu items={[
                        { label: 'View details', onClick: () => setOpenUser(u) },
                        ...(canManageUsers ? [{ label: 'Change role…', onClick: () => setRoleEditUser(u) }] : []),
                        { label: 'Copy email', onClick: () => { if (navigator.clipboard?.writeText) { navigator.clipboard.writeText(u.email).then(() => toast('Email copied')).catch(() => window.prompt('Email:', u.email)); } else { window.prompt('Email:', u.email); } } },
                      ]} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            )}
          </div>
        </div>
      )}

      {tab === 'pending' && (
        <div className="card">
          <div className="card-h">
            <h3>Pending registrations</h3>
            <div style={{ display: 'flex', gap: 6 }}>
              <select value={pendingSort} onChange={(e) => setPendingSort(e.target.value as 'recent' | 'az')} style={{ width: 'auto', height: 28 }}>
                <option value="recent">Sort: most recent</option>
                <option value="az">Sort: alphabetical</option>
              </select>
            </div>
          </div>
          <div className="card-b flush">
            {pending.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-2)', fontSize: 12.5 }}>✓ No pending registrations.</div>
            ) : (
              <table>
                <thead><tr><th>User</th><th>Affiliation</th><th>Position</th><th>Suggested role</th><th>Submitted</th><th>Training</th><th></th></tr></thead>
                <tbody>
                  {sortedPending.map((u) => (
                    <tr key={u.id}>
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <div className="avatar" style={{ background: 'var(--text-2)' }}>{u.name.split(' ').slice(-2).map((str: string) => str[0]).join('')}</div>
                          <div>
                            <div style={{ fontWeight: 500 }}>{u.name}</div>
                            <div style={{ fontSize: 10.5, color: 'var(--text-2)' }} className="mono">{u.email}</div>
                          </div>
                        </span>
                      </td>
                      <td style={{ fontSize: 11.5, color: 'var(--text-2)' }}>{u.affiliation}</td>
                      <td>{u.position}</td>
                      <td><span className="chip" data-tone="slate"><span className="dot" />{u.role}</span></td>
                      <td className="mono" style={{ fontSize: 11.5, color: 'var(--text-2)' }}>{u.submitted}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <span className="chip" data-tone={u.gcp ? 'green' : 'red'}><span className="dot" />GCP</span>
                          <span className="chip" data-tone={u.hsp ? 'green' : 'red'}><span className="dot" />HSP</span>
                          <span className="chip" data-tone={u.cv ? 'green' : 'red'}><span className="dot" />CV</span>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn sm" onClick={() => setOpen(u)} disabled={busyId === u.id}>Review</button>
                          <button className="btn primary sm" disabled={busyId === u.id} onClick={() => void doApprove(u.id, roleKeyFromLabel(u.role), u.role, 'BioCE — HEROs unit')}><Ico.check /></button>
                          <button className="btn sm" disabled={busyId === u.id} onClick={() => void doReject(u.id, u.name)} style={{ color: 'var(--accent)' }}><Ico.close /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {tab === 'suspended' && (
        <div className="card">
          <div className="card-h"><h3>Suspended accounts</h3></div>
          {suspendedUsers.length === 0 ? (
            <div className="card-b" style={{ padding: 40, textAlign: 'center', color: 'var(--text-2)' }}>
              <div style={{ fontSize: 14, marginBottom: 4 }}>No suspended accounts.</div>
              <div style={{ fontSize: 12 }}>Suspended users are those whose access has been paused but not deleted. They can be re-activated later.</div>
            </div>
          ) : (
            <div className="card-b flush">
              <table>
                <thead><tr><th>Member</th><th>Email</th><th>Role</th><th>Last login</th><th></th></tr></thead>
                <tbody>
                  {suspendedUsers.map((u) => {
                    const isSelf = u.id === currentUser?.id;
                    return (
                      <tr key={u.id}>
                        <td>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                            <Avatar user={u as any} />
                            <div style={{ fontWeight: 500 }}>{u.name}</div>
                          </span>
                        </td>
                        <td className="mono" style={{ fontSize: 11.5, color: 'var(--text-2)' }}>{u.email}</td>
                        <td><span className="chip" data-tone="violet"><span className="dot" />{u.role}</span></td>
                        <td className="mono" style={{ fontSize: 11.5, color: 'var(--text-2)' }}>{fmtLastLogin(u.lastLogin)}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                            <button className="btn sm" disabled={busyId === u.id} onClick={() => void doSetStatus(u, 'Active')}>Unsuspend</button>
                            <button className="btn sm" disabled={busyId === u.id || isSelf} title={isSelf ? 'Use Settings → Security to change your own password.' : undefined} onClick={() => void doResetPassword(u)}>Reset password</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'resets' && (
        <div className="card">
          <div className="card-h"><h3>Password reset requests</h3></div>
          <div className="card-b flush">
            {resets.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-2)', fontSize: 12.5 }}>✓ No pending reset requests.</div>
            ) : (
              <table>
                <thead><tr><th>Email</th><th>Matched account</th><th>Requested</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {resets.map((r) => (
                    <tr key={r.id}>
                      <td className="mono" style={{ fontSize: 11.5 }}>{r.email}</td>
                      <td>
                        {r.userId ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontWeight: 500 }}>{r.userName ?? '—'}</span>
                            {r.userStatus && <span className="chip" data-tone={r.userStatus === 'Suspended' ? 'red' : 'green'}><span className="dot" />{r.userStatus}</span>}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-3)', fontStyle: 'italic' }}>No matching account</span>
                        )}
                      </td>
                      <td className="mono" style={{ fontSize: 11.5, color: 'var(--text-2)' }}>{new Date(r.requestedAt).toLocaleString()}</td>
                      <td><span className="chip" data-tone={r.status === 'pending' ? 'amber' : 'slate'}><span className="dot" />{r.status}</span></td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                          {r.userId && (
                            <button className="btn primary sm" disabled={busyId === r.id} onClick={() => void doResetPassword({ id: r.userId, name: r.userName ?? r.email }, r.id)}>Reset password</button>
                          )}
                          <button className="btn ghost sm" disabled={busyId === r.id} onClick={() => void doDismissReset(r.id)}>Dismiss</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {tab === 'roles' && <RolesAndPermissions />}

      {open && (
        <ReviewModal
          pending={open}
          busy={busyId === open.id}
          onClose={() => setOpen(null)}
          onApprove={(roleKey, role, team) => void doApprove(open.id, roleKey, role, team)}
          onReject={() => void doReject(open.id, open.name)}
        />
      )}

      {roleEditUser && (
        <ChangeRoleModal
          user={roleEditUser}
          isSuper={isSuper}
          busy={busyId === roleEditUser.id}
          onClose={() => setRoleEditUser(null)}
          onSave={(roleKey, role) => void doSetRole(roleEditUser.id, roleKey, role)}
        />
      )}

      {openUser && (
        <Modal title={openUser.name} onClose={() => setOpenUser(null)} wide
          footer={(() => {
            const isSelf = openUser.id === currentUser?.id;
            const targetSuper = openUser.roleKey === 'super';
            const isSuspended = openUser.status === 'Suspended';
            const suspendBlocked = isSelf || (targetSuper && !isSuper);
            const suspendTitle = isSelf
              ? 'You cannot suspend your own account.'
              : (targetSuper && !isSuper ? 'Only a Super Admin can suspend another Super Admin.' : undefined);
            return (
              <>
                <button
                  className="btn ghost"
                  style={{ marginRight: 'auto', color: 'var(--accent)' }}
                  disabled={busyId === openUser.id || suspendBlocked}
                  title={suspendTitle}
                  onClick={() => void doSetStatus(openUser, isSuspended ? 'Active' : 'Suspended')}
                >{isSuspended ? 'Unsuspend account' : 'Suspend account'}</button>
                <button className="btn" onClick={() => { const id = openUser.id; setOpenUser(null); openProfile(id); }}><Ico.users /> View CV / certificates / signature</button>
                <button
                  className="btn"
                  disabled={busyId === openUser.id || isSelf}
                  title={isSelf ? 'Use Settings → Security to change your own password.' : undefined}
                  onClick={() => void doResetPassword(openUser)}
                >Reset password</button>
              </>
            );
          })()}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <h4 style={{ marginTop: 0, fontSize: 12, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Account</h4>
              <dl className="kv">
                <dt>Full name</dt><dd>{openUser.name}</dd>
                <dt>Email</dt><dd className="mono">{openUser.email}</dd>
                <dt>Role</dt><dd>{openUser.role}</dd>
                <dt>Status</dt><dd><span className="chip" data-tone="green"><span className="dot" />{openUser.status ?? 'Active'}</span></dd>
                <dt>Last login</dt><dd className="mono">{fmtLastLogin(openUser.lastLogin)}</dd>
                <dt>Created</dt><dd className="mono">2024-08-12</dd>
              </dl>
            </div>
            <div>
              <h4 style={{ marginTop: 0, fontSize: 12, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Project assignments</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {projects.filter((p) => p.members.includes(openUser.id)).map((p) => (
                  <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 8, background: 'var(--surface-alt)', borderRadius: 6 }}>
                    <div>
                      <div style={{ fontSize: 12.5, fontWeight: 500 }}><span className="mono">{p.code}</span> {p.shortTitle}</div>
                      <div style={{ fontSize: 10.5, color: 'var(--text-2)' }}>{p.id === openUser.id ? 'PI' : 'Member'}</div>
                    </div>
                    <span className="chip" data-tone="slate"><span className="dot" />{p.status}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {tempPw && (
        <TempPasswordModal name={tempPw.name} password={tempPw.password} onClose={() => setTempPw(null)} />
      )}
    </div>
  );
}

/** One-time display of a freshly-issued temporary password. The plaintext is
 *  cleared from state when the modal closes (shown once). */
function TempPasswordModal({ name, password, onClose }: { name: string; password: string; onClose: () => void }) {
  const toast = useToast();
  return (
    <Modal title={`Temporary password — ${name}`} onClose={onClose}
      footer={<button className="btn primary" onClick={onClose}><Ico.check /> Done</button>}>
      <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginBottom: 10 }}>
        Share this temporary password with the user over a secure channel. They should sign in and change it immediately.
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <code className="mono" style={{ flex: 1, padding: '10px 12px', background: 'var(--surface-alt)', borderRadius: 6, fontSize: 15, letterSpacing: '0.03em', userSelect: 'all' }}>{password}</code>
        <button className="btn" onClick={() => { void copyText(password).then((ok) => { if (ok) toast('Temporary password copied'); }); }}><Ico.dl /> Copy</button>
      </div>
      <div style={{ marginTop: 12, fontSize: 11.5, color: 'var(--accent)' }}>
        This password is shown only once. The user's old password no longer works.
      </div>
    </Modal>
  );
}

/** Change an existing user's role. Server enforces the super-only guards; the UI
 *  hides the Super Admin option from non-supers and blocks editing a Super target. */
function ChangeRoleModal({ user, isSuper, busy, onClose, onSave }: {
  user: any; isSuper: boolean; busy: boolean; onClose: () => void; onSave: (roleKey: Role, role: string) => void;
}) {
  const current = ROLE_DEFS.find((r) => r.id === user.roleKey) ?? ROLE_DEFS.find((r) => r.name === user.role) ?? ROLE_DEFS[2];
  const [roleId, setRoleId] = useState<Role>(current.id);
  const lockedToSuper = !isSuper && user.roleKey === 'super';
  const selectable = ROLE_DEFS.filter((r) => isSuper || r.id !== 'super');
  const chosen = ROLE_DEFS.find((r) => r.id === roleId) ?? current;
  return (
    <Modal title={`Change role — ${user.name}`} onClose={onClose}
      footer={(
        <>
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" disabled={busy || lockedToSuper || roleId === user.roleKey} onClick={() => onSave(roleId, chosen.name)}><Ico.check /> {busy ? 'Saving…' : 'Save role'}</button>
        </>
      )}>
      {lockedToSuper ? (
        <div style={{ fontSize: 12.5, color: 'var(--accent)' }}>Only a Super Admin can change another Super Admin’s role.</div>
      ) : (
        <>
          <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 10 }}>{user.name} · <span className="mono">{user.email}</span> — currently <strong>{user.role}</strong>.</div>
          <div className="field">
            <label>Assign role</label>
            <select value={roleId} onChange={(e) => setRoleId(e.target.value as Role)} disabled={busy}>
              {selectable.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
          <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-3)' }}>{chosen.desc}</div>
          {roleId === 'super' && <div style={{ marginTop: 8, fontSize: 11, color: '#8a5d12' }}>⚠ Super Admin has full system control — assign sparingly.</div>}
        </>
      )}
    </Modal>
  );
}

/** Review-and-approve modal for a single pending applicant (controlled role/team). */
function ReviewModal({
  pending,
  busy,
  onClose,
  onApprove,
  onReject,
}: {
  pending: PendingUser;
  busy: boolean;
  onClose: () => void;
  onApprove: (roleKey: Role, role: string, team: string) => void;
  onReject: () => void;
}) {
  const initialRole = ROLE_DEFS.find((r) => r.id === roleKeyFromLabel(pending.role)) ?? ROLE_DEFS[2];
  const [roleId, setRoleId] = useState<Role>(initialRole.id);
  const [team, setTeam] = useState('BioCE — HEROs unit');
  const roleName = ROLE_DEFS.find((r) => r.id === roleId)?.name ?? pending.role;

  return (
    <Modal title={`Review registration — ${pending.name}`} onClose={onClose} wide
      footer={(
        <>
          <button className="btn" onClick={onClose} disabled={busy}>Close</button>
          <button className="btn" style={{ color: 'var(--accent)' }} onClick={onReject} disabled={busy}>Reject</button>
          <button className="btn primary" onClick={() => onApprove(roleId, roleName, team)} disabled={busy}>
            <Ico.check /> {busy ? 'Working…' : 'Approve & activate'}
          </button>
        </>
      )}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div>
          <h4 style={{ marginTop: 0, fontSize: 12, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Account</h4>
          <dl className="kv">
            <dt>Full name</dt><dd>{pending.name}</dd>
            <dt>Email</dt><dd className="mono">{pending.email}</dd>
            <dt>Affiliation</dt><dd>{pending.affiliation}</dd>
            <dt>Position</dt><dd>{pending.position}</dd>
            <dt>Requested role</dt><dd>{pending.role}</dd>
            <dt>Submitted</dt><dd className="mono">{pending.submitted}</dd>
          </dl>
        </div>
        <div>
          <h4 style={{ marginTop: 0, fontSize: 12, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Training records</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {[
              { l: 'CV (PDF)', k: pending.cv },
              { l: 'Good Clinical Practice (GCP)', k: pending.gcp },
              { l: 'Human Subject Protection (HSP)', k: pending.hsp },
            ].map((row, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 8, background: 'var(--surface-alt)', borderRadius: 6 }}>
                <span style={{ fontSize: 12.5 }}>{row.l}</span>
                {row.k ? <span className="chip" data-tone="green"><span className="dot" />Uploaded</span> : <span className="chip" data-tone="red"><span className="dot" />Missing</span>}
              </div>
            ))}
          </div>
          <h4 style={{ marginTop: 16, fontSize: 12, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Assignment</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div className="field">
              <label>Role</label>
              <select value={roleId} onChange={(e) => setRoleId(e.target.value as Role)} disabled={busy}>
                {ROLE_DEFS.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Team</label>
              <select value={team} onChange={(e) => setTeam(e.target.value)} disabled={busy}>
                <option>BioCE — HEROs unit</option>
                <option>External collaborator</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function RolesAndPermissions() {
  const [edit, setEdit] = useState<any>(null);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 14 }}>
      <div className="card">
        <div className="card-h">
          <h3>Roles ({ROLE_DEFS.length})</h3>
        </div>
        <div className="card-b" style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: 8 }}>
          {ROLE_DEFS.map((r) => (
            <div key={r.id} className="hover-row" style={{ padding: '8px 10px', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }} onClick={() => setEdit(r)}>
              <span className="chip" data-tone={r.color} style={{ width: 10, height: 10, padding: 0, borderRadius: 50 }}><span className="dot" style={{ background: 'transparent' }} /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 12.5 }}>{r.name}</div>
                <div style={{ fontSize: 10.5, color: 'var(--text-2)' }}>{r.users} active user{r.users === 1 ? '' : 's'}</div>
              </div>
              <Ico.chev />
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <div className="card-h">
          <h3>Permission matrix</h3>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn ghost sm" onClick={() => {
              const rows = CAPS.flatMap((g) => g.caps.map((c) => ({ ...c, group: g.group })));
              const cols: Record<string, (r: (typeof rows)[number]) => unknown> = {
                Group: (r) => r.group,
                Capability: (r) => r.label,
                Id: (r) => r.id,
              };
              for (const rd of ROLE_DEFS) {
                cols[rd.name] = (r) => { const v = (r.roles as any)[rd.id]; return v === true ? 'allow' : v === 'view' ? 'view' : 'deny'; };
              }
              downloadCsv(rows, cols, `permission-matrix-${dateStamp()}.csv`);
            }}><Ico.dl /> Export CSV</button>
          </div>
        </div>
        <div className="card-b flush scroll-x">
          <table style={{ minWidth: 920 }}>
            <thead>
              <tr>
                <th style={{ width: 280, position: 'sticky', left: 0, background: 'var(--surface-alt)', zIndex: 2 }}>Capability</th>
                {ROLE_DEFS.map((r) => (
                  <th key={r.id} title={r.name} style={{ textAlign: 'center', minWidth: 64, fontSize: 10, lineHeight: 1.1, padding: '6px 4px' }}>
                    {r.name.split(' ').slice(0, 2).join(' ')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {CAPS.map((group) => (
                <React.Fragment key={group.group}>
                  <tr>
                    <td colSpan={ROLE_DEFS.length + 1} style={{ background: 'var(--surface-alt)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-2)' }}>
                      {group.group}
                    </td>
                  </tr>
                  {group.caps.map((cap) => (
                    <tr key={cap.id}>
                      <td style={{ position: 'sticky', left: 0, background: 'var(--surface)', zIndex: 1 }}>
                        <div style={{ fontWeight: 500, fontSize: 12 }}>{cap.label}</div>
                        <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 1 }}>{cap.id}</div>
                      </td>
                      {ROLE_DEFS.map((r) => {
                        const val = (cap.roles as any)[r.id];
                        return (
                          <td key={r.id} style={{ textAlign: 'center', padding: '4px 2px' }}>
                            {val === true && <span title="Allowed" style={{ color: '#2E6A3C', fontWeight: 700 }}>✓</span>}
                            {val === 'view' && <span className="chip" data-tone="blue" style={{ fontSize: 9, padding: '1px 5px' }}>view</span>}
                            {!val && <span style={{ color: 'var(--text-3)' }}>·</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ padding: 10, fontSize: 11, color: 'var(--text-2)', borderTop: '1px solid var(--border)', display: 'flex', gap: 16 }}>
          <span><span style={{ color: '#2E6A3C', fontWeight: 700 }}>✓</span> Allowed</span>
          <span><span className="chip" data-tone="blue" style={{ fontSize: 9, padding: '1px 5px' }}>view</span> Read-only</span>
          <span><span style={{ color: 'var(--text-3)' }}>·</span> Denied</span>
        </div>
      </div>

      {edit && (
        <Modal title={`${edit.name} — role definition (read-only)`} onClose={() => setEdit(null)}
          footer={<button className="btn" onClick={() => setEdit(null)}>Close</button>}>
          <div className="field">
            <label>Display name</label>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{edit.name}</div>
          </div>
          <div className="field" style={{ marginTop: 10 }}>
            <label>Description</label>
            <div style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{edit.desc}</div>
          </div>
          <div className="field" style={{ marginTop: 10 }}>
            <label>Capabilities granted ({CAPS.flatMap((g) => g.caps).filter((c) => (c.roles as any)[edit.id]).length})</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 280, overflow: 'auto', border: '1px solid var(--border)', borderRadius: 6, padding: 8 }}>
              {CAPS.flatMap((g) => g.caps.map((c) => ({ ...c, group: g.group }))).map((c) => {
                const has = (c.roles as any)[edit.id];
                return (
                  <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 4, fontSize: 12 }}>
                    <input type="checkbox" checked={!!has} disabled readOnly />
                    <span style={{ flex: 1 }}>{c.label}</span>
                    <span style={{ fontSize: 10, color: 'var(--text-3)' }}>{c.group}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/** Inline editor for a protocol pricing row (create or update). */
function ProtocolEditor({ row, onClose, onSaved }: { row: any; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [form, setForm] = useState<any>({ level: row.level || '', cost: row.cost || 0, desc: row.desc || '', active: row.active ?? true });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      if (row.id) await apiClient.pricing.protocol.update(row.id, { level: form.level, cost: +form.cost || 0, desc: form.desc, active: form.active });
      else await apiClient.pricing.protocol.create({ level: form.level, cost: +form.cost || 0, desc: form.desc, active: form.active });
      toast('Pricing saved'); onSaved(); onClose();
    } catch (err) {
      toast(err instanceof ApiClientError ? (err.body?.message ?? 'Save failed') : 'Save failed');
    } finally { setBusy(false); }
  };
  return (
    <Modal title={row.id ? 'Edit protocol level' : 'New protocol level'} onClose={onClose}
      footer={<><button className="btn" onClick={onClose} disabled={busy}>Cancel</button><button className="btn primary" onClick={() => void save()} disabled={busy || !form.level}><Ico.check /> {busy ? 'Saving…' : 'Save'}</button></>}>
      <div className="field"><label>Level</label><input value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} placeholder="e.g. Complex" /></div>
      <div className="field" style={{ marginTop: 10 }}><label>Description</label><input value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} /></div>
      <div className="field" style={{ marginTop: 10 }}><label>Cost (฿)</label><input type="number" className="tabular mono" value={form.cost} onChange={(e) => setForm({ ...form, cost: +e.target.value || 0 })} /></div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 12.5 }}>
        <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active
      </label>
    </Modal>
  );
}

/** Inline editor for a descriptive-analysis pricing band (create or update). */
function DescriptiveEditor({ row, onClose, onSaved }: { row: any; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [form, setForm] = useState<any>({ range: row.range || '', cost: row.cost || 0, active: row.active ?? true });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      if (row.id) await apiClient.pricing.descriptive.update(row.id, { range: form.range, cost: +form.cost || 0, active: form.active });
      else await apiClient.pricing.descriptive.create({ range: form.range, cost: +form.cost || 0, active: form.active });
      toast('Pricing band saved'); onSaved(); onClose();
    } catch (err) {
      toast(err instanceof ApiClientError ? (err.body?.message ?? 'Save failed') : 'Save failed');
    } finally { setBusy(false); }
  };
  return (
    <Modal title={row.id ? 'Edit descriptive band' : 'New descriptive band'} onClose={onClose}
      footer={<><button className="btn" onClick={onClose} disabled={busy}>Cancel</button><button className="btn primary" onClick={() => void save()} disabled={busy || !form.range}><Ico.check /> {busy ? 'Saving…' : 'Save'}</button></>}>
      <div className="field"><label>Range</label><input value={form.range} onChange={(e) => setForm({ ...form, range: e.target.value })} placeholder="e.g. 101 – 120" /></div>
      <div className="field" style={{ marginTop: 10 }}><label>Cost (฿)</label><input type="number" className="tabular mono" value={form.cost} onChange={(e) => setForm({ ...form, cost: +e.target.value || 0 })} /></div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 12.5 }}>
        <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active
      </label>
    </Modal>
  );
}

/** Inline editor for a sample-size top-up rule (create or update). */
function TopupEditor({ row, onClose, onSaved }: { row: any; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [form, setForm] = useState<any>({ range: row.range || '', rate: row.rate || '', active: row.active ?? true });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      if (row.id) await apiClient.pricing.topup.update(row.id, { range: form.range, rate: form.rate, active: form.active });
      else await apiClient.pricing.topup.create({ range: form.range, rate: form.rate, active: form.active });
      toast('Top-up rule saved'); onSaved(); onClose();
    } catch (err) {
      toast(err instanceof ApiClientError ? (err.body?.message ?? 'Save failed') : 'Save failed');
    } finally { setBusy(false); }
  };
  return (
    <Modal title={row.id ? 'Edit top-up rule' : 'New top-up rule'} onClose={onClose}
      footer={<><button className="btn" onClick={onClose} disabled={busy}>Cancel</button><button className="btn primary" onClick={() => void save()} disabled={busy || !form.range}><Ico.check /> {busy ? 'Saving…' : 'Save'}</button></>}>
      <div className="field"><label>Sample-size range</label><input value={form.range} onChange={(e) => setForm({ ...form, range: e.target.value })} placeholder="e.g. 101 – 400" /></div>
      <div className="field" style={{ marginTop: 10 }}><label>Top-up rate</label><input value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} placeholder="e.g. 10%" /></div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 12.5 }}>
        <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active
      </label>
    </Modal>
  );
}

/** Editor for the singleton analysis-cost formula. Ranges mirror the server's
 *  validation (minutes 1–600, rate 0–1,000,000, multiplier 0.1–10). */
function FormulaEditor({ formula, onClose, onSaved }: { formula: AnalysisFormula; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [form, setForm] = useState({
    minutesPerSimple: formula.minutesPerSimple,
    minutesPerComplex: formula.minutesPerComplex,
    hourlyRate: formula.hourlyRate,
    complexMultiplier: formula.complexMultiplier,
  });
  const [busy, setBusy] = useState(false);
  const inRange = (v: number, lo: number, hi: number) => Number.isFinite(v) && v >= lo && v <= hi;
  const valid =
    inRange(+form.minutesPerSimple, 1, 600) &&
    inRange(+form.minutesPerComplex, 1, 600) &&
    inRange(+form.hourlyRate, 0, 1000000) &&
    inRange(+form.complexMultiplier, 0.1, 10);
  const save = async () => {
    setBusy(true);
    try {
      await apiClient.analysisFormula.update({
        minutesPerSimple: +form.minutesPerSimple,
        minutesPerComplex: +form.minutesPerComplex,
        hourlyRate: +form.hourlyRate,
        complexMultiplier: +form.complexMultiplier,
      });
      toast('Analysis formula saved'); onSaved(); onClose();
    } catch (err) {
      toast(err instanceof ApiClientError ? (err.body?.message ?? 'Save failed') : 'Save failed');
    } finally { setBusy(false); }
  };
  return (
    <Modal title="Edit analysis formula" onClose={onClose}
      footer={<><button className="btn" onClick={onClose} disabled={busy}>Cancel</button><button className="btn primary" onClick={() => void save()} disabled={busy || !valid}><Ico.check /> {busy ? 'Saving…' : 'Save'}</button></>}>
      <div className="field"><label>Minutes per simple unit (1–600)</label><input type="number" min={1} max={600} className="tabular mono" value={form.minutesPerSimple} onChange={(e) => setForm({ ...form, minutesPerSimple: +e.target.value || 0 })} /></div>
      <div className="field" style={{ marginTop: 10 }}><label>Minutes per complex unit (1–600)</label><input type="number" min={1} max={600} className="tabular mono" value={form.minutesPerComplex} onChange={(e) => setForm({ ...form, minutesPerComplex: +e.target.value || 0 })} /></div>
      <div className="field" style={{ marginTop: 10 }}><label>Hourly rate ฿ (0–1,000,000)</label><input type="number" min={0} max={1000000} className="tabular mono" value={form.hourlyRate} onChange={(e) => setForm({ ...form, hourlyRate: +e.target.value || 0 })} /></div>
      <div className="field" style={{ marginTop: 10 }}><label>Complex multiplier (0.1–10)</label><input type="number" step={0.05} min={0.1} max={10} className="tabular mono" value={form.complexMultiplier} onChange={(e) => setForm({ ...form, complexMultiplier: +e.target.value || 0 })} /></div>
      <div style={{ marginTop: 10, fontSize: 11.5, color: valid ? 'var(--text-2)' : 'var(--accent)' }}>
        {valid ? 'Edits affect only new quotations. Existing quotes preserve their pricing snapshot.' : 'Values must be within the allowed ranges.'}
      </div>
    </Modal>
  );
}

export function AdminPricing() {
  const D = useData();
  const { reload } = useDataActions();
  const toast = useToast();
  const [tab, setTab] = useState('protocol');
  const [editRow, setEditRow] = useState<any>(null);
  const [editDesc, setEditDesc] = useState<any>(null);
  const [editTopup, setEditTopup] = useState<any>(null);
  const [editFormula, setEditFormula] = useState(false);
  const canEdit = useCan('sys.pricing.edit') === 'allow';
  const fx: AnalysisFormula = D.analysisFormula;

  const delProtocol = async (id: string) => {
    if (!window.confirm('Delete this pricing row?')) return;
    try { await apiClient.pricing.protocol.remove(id); await reload(); toast('Pricing row deleted'); }
    catch (err) { toast(err instanceof ApiClientError ? (err.body?.message ?? 'Delete failed') : 'Delete failed'); }
  };
  const delDescriptive = async (id: string) => {
    if (!window.confirm('Delete this pricing band?')) return;
    try { await apiClient.pricing.descriptive.remove(id); await reload(); toast('Band deleted'); }
    catch (err) { toast(err instanceof ApiClientError ? (err.body?.message ?? 'Delete failed') : 'Delete failed'); }
  };
  const delTopup = async (id: string) => {
    if (!window.confirm('Delete this top-up rule?')) return;
    try { await apiClient.pricing.topup.remove(id); await reload(); toast('Rule deleted'); }
    catch (err) { toast(err instanceof ApiClientError ? (err.body?.message ?? 'Delete failed') : 'Delete failed'); }
  };

  return (
    <div className="page-pad fadein">
      <div className="page-head">
        <div>
          <h2>Pricing Library</h2>
          <div className="sub">All quotation prices and formulas — admin configurable, no programmer required</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={() => {
            type LibRow = { Section: string; Name: string; Description?: string; Unit?: string; Price?: number | string; Active: boolean };
            const rows: LibRow[] = [
              ...D.pricingProtocol.map((p) => ({ Section: 'Protocol', Name: p.level, Description: p.desc, Price: p.cost, Active: p.active })),
              ...D.pricingDescriptive.map((b) => ({ Section: 'Descriptive', Name: b.range, Price: b.cost, Active: b.active !== false })),
              ...D.pricingTopup.map((t) => ({ Section: 'Top-up', Name: t.range, Price: t.rate, Active: t.active !== false })),
              ...(D.catalog as any[]).map((c) => ({ Section: c.kind as string, Name: c.name as string, Description: c.groupName as string | undefined, Unit: c.unit as string | undefined, Price: c.price as number | undefined, Active: c.status === 'active' })),
              ...(D.dmCatalog as any[]).map((d) => ({ Section: 'DM ' + d.category, Name: d.name as string, Unit: d.unit as string | undefined, Price: (d.isFixedMin ? d.fixedCost : d.unitPrice) as number | undefined, Active: d.active !== false })),
            ];
            downloadCsv(rows, {
              Section: (r) => r.Section,
              Name: (r) => r.Name,
              Description: (r) => r.Description ?? '',
              Unit: (r) => r.Unit ?? '',
              'Price/Rate (THB)': (r) => r.Price ?? '',
              Active: (r) => (r.Active ? 'Yes' : 'No'),
            }, `pricing-library-${dateStamp()}.csv`);
          }}><Ico.dl /> Export library</button>
        </div>
      </div>

      <div className="tabs">
        {[
          ['protocol', 'Protocol pricing'],
          ['dmp', 'DMP items'],
          ['crf', 'CRF / eCRF'],
          ['lab', 'Lab / procedures'],
          ['variables', 'Non-lab variables'],
          ['outcomes', 'Outcome templates'],
          ['analysis', 'Analysis rules'],
          ['topup', 'Sample-size top-up'],
          ['addons', 'Add-on services'],
        ].map(([id, label]) => (
          <button key={id} className={'tab' + (tab === id ? ' active' : '')} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>

      {tab === 'protocol' && (
        <div className="card">
          <div className="card-h"><h3>Protocol complexity levels</h3>{canEdit && <button className="btn primary sm" onClick={() => setEditRow({})}><Ico.plus /> Level</button>}</div>
          <div className="card-b flush">
            <table>
              <thead><tr><th>Level</th><th>Description</th><th className="num">Cost (฿)</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {D.pricingProtocol.map((p) => (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 600 }}>{p.level}</td>
                    <td style={{ color: 'var(--text-2)' }}>{p.desc}</td>
                    <td className="num tabular mono">{p.cost.toLocaleString()}</td>
                    <td>{p.active ? <span className="chip" data-tone="green"><span className="dot" />Active</span> : <span className="chip"><span className="dot" />Inactive</span>}</td>
                    <td><div style={{ display: 'flex', gap: 2 }}>
                      <button className="btn ghost icon sm" disabled={!canEdit} onClick={() => setEditRow(p)}><Ico.edit /></button>
                      <button className="btn ghost icon sm" disabled={!canEdit} onClick={() => void delProtocol(p.id)}><Ico.trash /></button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {editRow && <ProtocolEditor row={editRow} onClose={() => setEditRow(null)} onSaved={() => void reload()} />}
        </div>
      )}

      {tab === 'analysis' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 14 }}>
          <div className="card">
            <div className="card-h"><h3>Descriptive analysis pricing bands</h3>{canEdit && <button className="btn primary sm" onClick={() => setEditDesc({})}><Ico.plus /> Band</button>}</div>
            <div className="card-b flush">
              <table>
                <thead><tr><th>Range</th><th className="num">Cost (฿)</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {D.pricingDescriptive.map((b) => (
                    <tr key={b.id}>
                      <td>{b.range}</td>
                      <td className="num tabular mono">{b.cost.toLocaleString()}</td>
                      <td>{b.active === false ? <span className="chip"><span className="dot" />Inactive</span> : <span className="chip" data-tone="green"><span className="dot" />Active</span>}</td>
                      <td><div style={{ display: 'flex', gap: 2 }}>
                        <button className="btn ghost icon sm" disabled={!canEdit} onClick={() => setEditDesc(b)}><Ico.edit /></button>
                        <button className="btn ghost icon sm" disabled={!canEdit} onClick={() => void delDescriptive(b.id)}><Ico.trash /></button>
                      </div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {editDesc && <DescriptiveEditor row={editDesc} onClose={() => setEditDesc(null)} onSaved={() => void reload()} />}
          </div>
          <div className="card">
            <div className="card-h"><h3>Objective-based analysis</h3></div>
            <div className="card-b">
              <dl className="kv">
                <dt>Simple analysis</dt><dd><span className="mono tabular">{fx.minutesPerSimple}</span> min / unit</dd>
                <dt>Complex analysis</dt><dd><span className="mono tabular">{fx.minutesPerComplex}</span> min / unit</dd>
                <dt>Hourly rate</dt><dd><span className="mono tabular">฿{fx.hourlyRate.toLocaleString()}</span> / hour</dd>
                <dt>Complex multiplier</dt><dd><span className="mono tabular">× {fx.complexMultiplier.toFixed(2)}</span></dd>
                <dt>Default mode</dt><dd>Per-unit calculation</dd>
              </dl>
              <hr className="rule" />
              <div style={{ fontSize: 11.5, color: 'var(--text-2)' }}>Edits affect only <strong>new</strong> quotations. Existing quotes preserve their pricing snapshot.</div>
              {canEdit && <button className="btn sm" style={{ marginTop: 8 }} onClick={() => setEditFormula(true)}><Ico.edit /> Edit formula</button>}
            </div>
          </div>
          {editFormula && <FormulaEditor formula={fx} onClose={() => setEditFormula(false)} onSaved={() => void reload()} />}
        </div>
      )}

      {tab === 'topup' && (
        <div className="card">
          <div className="card-h"><h3>Sample-size top-up rules</h3>{canEdit && <button className="btn primary sm" onClick={() => setEditTopup({})}><Ico.plus /> Rule</button>}</div>
          <div className="card-b flush">
            <table>
              <thead><tr><th>Sample size range</th><th>Top-up rate</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {D.pricingTopup.map((r) => (
                  <tr key={r.id}>
                    <td>{r.range}</td>
                    <td className="mono tabular">{r.rate}</td>
                    <td>{r.active === false ? <span className="chip"><span className="dot" />Inactive</span> : <span className="chip" data-tone="green"><span className="dot" />Active</span>}</td>
                    <td><div style={{ display: 'flex', gap: 2 }}>
                      <button className="btn ghost icon sm" disabled={!canEdit} onClick={() => setEditTopup(r)}><Ico.edit /></button>
                      <button className="btn ghost icon sm" disabled={!canEdit} onClick={() => void delTopup(r.id)}><Ico.trash /></button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {editTopup && <TopupEditor row={editTopup} onClose={() => setEditTopup(null)} onSaved={() => void reload()} />}
        </div>
      )}

      {(tab === 'lab' || tab === 'variables') && (
        <CatalogLibrary kinds={tab === 'lab' ? ['lab', 'procedure'] : ['variable']} />
      )}
      {(tab === 'dmp' || tab === 'crf') && (
        <DmCatalogLibrary category={tab === 'dmp' ? 'DMP' : 'CRF'} />
      )}
      {tab === 'outcomes' && <CatalogLibrary kinds={['outcome']} label="Outcome template library" />}
      {tab === 'addons' && <CatalogLibrary kinds={['addon']} label="Add-on service library" />}
    </div>
  );
}

/** Real catalog library + admin promotion queue (Topic A). `kinds` scopes the tab. */
function CatalogLibrary({ kinds, label }: { kinds: string[]; label?: string }) {
  const D = useData();
  const { createCatalogItem, approveCatalogItem, rejectCatalogItem, removeCatalogItem } = useDataActions();
  const toast = useToast();
  const isVar = kinds.includes('variable');
  // "Group of variable" dropdown options (senior request): canonical VAR_GROUPS +
  // any group already used by a library variable. Rendered as a datalist so a
  // custom group can still be typed.
  const varGroupOptions = [...new Set([
    ...VAR_GROUPS,
    ...(D.catalog as any[]).filter((c) => c.kind === 'variable' && c.groupName).map((c) => c.groupName as string),
  ])].filter(Boolean);
  // Lab/procedure prices are no longer used (senior request) — hide the price
  // column/inputs for those kinds (variables never had a price). Outcome/add-on
  // libraries keep their price.
  const isLabKind = kinds.some((k) => k === 'lab' || k === 'procedure');
  const showPrice = !isVar && !isLabKind;
  const items = (D.catalog as any[]).filter((c) => kinds.includes(c.kind));
  const pending = items.filter((c) => c.status === 'pending');
  const active = items.filter((c) => c.status === 'active');
  // Search + sort the active library (default: grouped by category for readability).
  const [q, setQ] = useState('');
  const [sortKey, setSortKey] = useState<'name' | 'code' | 'groupName' | 'price'>('groupName');
  const [sortDir, setSortDir] = useState<1 | -1>(1);
  const norm = (s: any) => (s == null ? '' : String(s)).toLowerCase();
  const shown = active
    .filter((c) => !q.trim() || [c.name, c.code, c.groupName].some((f) => norm(f).includes(q.trim().toLowerCase())))
    .sort((a, b) => {
      const av = sortKey === 'price' ? (a.price ?? 0) : norm((a as any)[sortKey]);
      const bv = sortKey === 'price' ? (b.price ?? 0) : norm((b as any)[sortKey]);
      return (av < bv ? -1 : av > bv ? 1 : 0) * sortDir;
    });
  const sortBy = (key: typeof sortKey) => () => { if (sortKey === key) setSortDir((d) => (d === 1 ? -1 : 1)); else { setSortKey(key); setSortDir(1); } };
  const arrow = (key: typeof sortKey) => (sortKey === key ? (sortDir === 1 ? ' ▲' : ' ▼') : '');
  const thBtn: React.CSSProperties = { background: 'none', border: 0, font: 'inherit', fontWeight: 600, color: 'inherit', cursor: 'pointer', padding: 0 };
  const [edits, setEdits] = useState<Record<string, any>>({});
  const ed = (id: string) => edits[id] || {};
  const setEd = (id: string, k: string, v: any) => setEdits((e) => ({ ...e, [id]: { ...e[id], [k]: v } }));
  // Direct "Add item" to the databank — admin-created items land straight in the
  // ACTIVE library in ONE call (POST /api/catalog), so they appear in project search.
  const [adding, setAdding] = useState(false);
  const blankDraft = () => ({ name: '', code: '', groupName: '', price: 0, kind: kinds[0] });
  const [draft, setDraft] = useState<any>(blankDraft);
  const [savingAdd, setSavingAdd] = useState(false);
  const addItem = async () => {
    const name = String(draft.name || '').trim();
    if (!name) { toast('Item name is required'); return; }
    setSavingAdd(true);
    try {
      await createCatalogItem({
        kind: draft.kind,
        name,
        code: draft.code?.trim() || undefined,
        groupName: draft.groupName?.trim() || undefined,
        ...(showPrice ? { price: +draft.price || 0 } : {}),
      });
      toast('Added to the library');
      setDraft(blankDraft()); setAdding(false);
    } catch (err: any) { toast(err?.message || 'Could not add item'); }
    finally { setSavingAdd(false); }
  };
  const approve = async (c: any) => {
    const e = ed(c.id);
    try {
      await approveCatalogItem(c.id, {
        name: e.name ?? c.name,
        code: e.code ?? c.code,
        groupName: e.groupName ?? c.groupName,
        ...(showPrice ? { price: e.price !== undefined ? +e.price : (c.price ?? 0) } : {}),
      });
      toast('Published to the library');
    } catch (err: any) { toast(err?.message || 'Approve failed'); }
  };
  const reject = async (c: any) => {
    try { await rejectCatalogItem(c.id, ed(c.id).note); toast('Rejected — kept project-local'); }
    catch (err: any) { toast(err?.message || 'Reject failed'); }
  };
  const del = async (c: any) => { try { await removeCatalogItem(c.id); toast('Removed from library'); } catch (err: any) { toast(err?.message || 'Delete failed'); } };
  const title = label ?? (isVar ? 'Non-lab variable library' : 'Lab & procedure library');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {isVar && <datalist id="admin-var-groups">{varGroupOptions.map((g) => <option key={g} value={g} />)}</datalist>}
      {pending.length > 0 && (
        <div className="card" style={{ borderColor: '#caa24a' }}>
          <div className="card-h"><h3>Pending promotion ({pending.length})</h3><span className="chip" data-tone="amber"><span className="dot" />Awaiting review</span></div>
          <div className="card-b flush">
            <table>
              <thead><tr><th>Name</th><th>Code</th><th>Group</th>{showPrice && <th className="num">฿</th>}<th>Proposed in</th><th></th></tr></thead>
              <tbody>
                {pending.map((c) => (
                  <tr key={c.id}>
                    <td><input value={ed(c.id).name ?? c.name} onChange={(e) => setEd(c.id, 'name', e.target.value)} /></td>
                    <td><input className="mono" style={{ width: 110 }} placeholder={isVar ? 'VAR-…' : 'LAB-…'} value={ed(c.id).code ?? c.code ?? ''} onChange={(e) => setEd(c.id, 'code', e.target.value)} /></td>
                    <td><input style={{ width: 130 }} {...(isVar ? { list: 'admin-var-groups' } : {})} value={ed(c.id).groupName ?? c.groupName ?? ''} onChange={(e) => setEd(c.id, 'groupName', e.target.value)} /></td>
                    {showPrice && <td className="num"><input type="number" className="tabular mono" style={{ width: 70, textAlign: 'right' }} value={ed(c.id).price ?? c.price ?? 0} onChange={(e) => setEd(c.id, 'price', e.target.value)} /></td>}
                    <td className="mono" style={{ fontSize: 11, color: 'var(--text-2)' }}>{c.proposedInProject || '—'}</td>
                    <td><div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn primary sm" onClick={() => approve(c)}>Approve</button>
                      <button className="btn ghost sm" onClick={() => reject(c)}>Reject</button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <div className="card">
        <div className="card-h">
          <h3>{title}</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span className="chip" data-tone="green"><span className="dot" />{active.length} active</span>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / code / group…" style={{ height: 28, width: 200 }} />
            <button className="btn primary sm" onClick={() => setAdding((v) => !v)}><Ico.plus /> Add item</button>
          </div>
        </div>
        {adding && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end', padding: '10px 14px', borderBottom: '1px solid var(--border)', background: 'var(--surface-alt)' }}>
            <div className="field" style={{ flex: 2, minWidth: 160 }}><label>Item name *</label><input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder={isVar ? 'e.g. HbA1c' : 'e.g. Lipid profile'} /></div>
            <div className="field" style={{ width: 120 }}><label>Code</label><input className="mono" value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} placeholder={isVar ? 'VAR-…' : 'LAB-…'} /></div>
            <div className="field" style={{ width: 170 }}><label>{isVar ? 'Group of variable' : 'Group'}</label><input {...(isVar ? { list: 'admin-var-groups' } : {})} value={draft.groupName} onChange={(e) => setDraft({ ...draft, groupName: e.target.value })} placeholder={isVar ? 'pick or type a group' : 'e.g. Endocrine'} /></div>
            {kinds.length > 1 && <div className="field" style={{ width: 90 }}><label>Kind</label><select value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value })}>{kinds.map((k) => <option key={k} value={k}>{k}</option>)}</select></div>}
            {showPrice && <div className="field" style={{ width: 100 }}><label>฿ / unit</label><input type="number" className="tabular mono" value={draft.price} onChange={(e) => setDraft({ ...draft, price: +e.target.value || 0 })} /></div>}
            <button className="btn primary sm" disabled={savingAdd || !draft.name.trim()} onClick={() => void addItem()}><Ico.check /> {savingAdd ? 'Adding…' : 'Save to library'}</button>
            <button className="btn ghost sm" disabled={savingAdd} onClick={() => { setAdding(false); setDraft(blankDraft()); }}>Cancel</button>
          </div>
        )}
        <div className="card-b flush">
          <table>
            <thead><tr>
              <th><button style={thBtn} onClick={sortBy('name')}>Item{arrow('name')}</button></th>
              <th><button style={thBtn} onClick={sortBy('code')}>Code{arrow('code')}</button></th>
              <th><button style={thBtn} onClick={sortBy('groupName')}>Group{arrow('groupName')}</button></th>
              <th>Kind</th>
              {showPrice && <th className="num"><button style={thBtn} onClick={sortBy('price')}>฿{arrow('price')}</button></th>}
              <th className="num">Params</th><th></th>
            </tr></thead>
            <tbody>
              {shown.map((c) => (
                <tr key={c.id}>
                  <td style={{ fontWeight: 500 }}>{c.name}</td>
                  <td className="mono" style={{ fontSize: 11.5, color: 'var(--text-2)' }}>{c.code || '—'}</td>
                  <td>{c.groupName || '—'}</td>
                  <td style={{ fontSize: 11 }}>{c.kind}</td>
                  {showPrice && <td className="num tabular mono">{c.price ?? '—'}</td>}
                  <td className="num tabular mono" title={(c.parameters || []).map((p: any) => p.name).join(', ')}>{c.parameters?.length || '—'}</td>
                  <td><button className="btn ghost icon sm" title="Remove from library" onClick={() => del(c)}><Ico.trash /></button></td>
                </tr>
              ))}
              {shown.length === 0 && <tr><td colSpan={showPrice ? 7 : 6} style={{ padding: 20, textAlign: 'center', color: 'var(--text-2)' }}>{active.length === 0 ? 'No items yet.' : 'No items match your search.'}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/** DMP / CRF pricing — rendered from the canonical DM_CATALOG (transcribed from the
 *  Data_Management_cost_HERO_team_2026 sheet), so the figures match the source of truth
 *  used by the quotation engine. Read-only + searchable. */
function DmCatalogLibrary({ category }: { category: 'DMP' | 'CRF' }) {
  const D = useData();
  const { createDmItem, updateDmItem, removeDmItem, reload } = useDataActions();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [seeding, setSeeding] = useState(false);
  const seedDatabank = async () => {
    setSeeding(true);
    try { const r = await apiClient.dmCatalog.seed(); await reload(); toast(`Seeded ${r.seeded} items — now editable`); }
    catch (e: any) { toast(e?.message || 'Seed failed'); }
    finally { setSeeding(false); }
  };
  // Editable DB databank when seeded; else the built-in constant (read-only).
  const dbItems = (D.dmCatalog || []).filter((it: any) => it.category === category);
  const editable = dbItems.length > 0;
  const source: any[] = editable ? dbItems : DM_CATALOG.filter((it) => it.category === category && it.active);
  const shown = q.trim() ? source.filter((r: any) => r.name.toLowerCase().includes(q.trim().toLowerCase())) : source;
  const title = category === 'DMP' ? 'Data Management Plan (DMP) items' : 'CRF / eCRF items';
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<any>({ name: '', unit: 'per man-hour', estUnits: 1, unitPrice: 1000 });
  const patch = async (id: string, body: any) => { try { await updateDmItem(id, body); } catch (e: any) { toast(e?.message || 'Save failed'); } };
  const add = async () => {
    if (!draft.name.trim()) { toast('Item name is required'); return; }
    try {
      await createDmItem({ category, name: draft.name.trim(), unit: draft.unit || undefined, estUnits: +draft.estUnits || 0, unitPrice: +draft.unitPrice || 0, sortOrder: 999, active: true });
      toast('Added to the DM databank'); setDraft({ name: '', unit: 'per man-hour', estUnits: 1, unitPrice: 1000 }); setAdding(false);
    } catch (e: any) { toast(e?.message || 'Add failed'); }
  };
  return (
    <div className="card">
      <div className="card-h">
        <h3>{title}</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span className="chip" data-tone="green"><span className="dot" />{source.length} items</span>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" style={{ height: 28, width: 170 }} />
          {editable && <button className="btn primary sm" onClick={() => setAdding((v) => !v)}><Ico.plus /> Add item</button>}
          {!editable && <button className="btn primary sm" disabled={seeding} onClick={() => void seedDatabank()}>{seeding ? 'Seeding…' : 'Seed databank from defaults'}</button>}
        </div>
      </div>
      <div style={{ padding: '8px 14px', fontSize: 11.5, color: editable ? 'var(--text-2)' : 'var(--accent)', borderBottom: '1px solid var(--border)' }}>
        {editable
          ? <>The quotation engine prices DMP/CRF from these rows. Edit Est. units / Unit ฿ inline (saves on blur). <strong>min cost</strong> rows are lump-sum alternatives.</>
          : <>Databank not seeded yet — showing the built-in defaults (read-only). Click <strong>Seed databank from defaults</strong> to make these items editable (quotation totals are unchanged — the seed matches the current figures).</>}
      </div>
      {editable && adding && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end', padding: '10px 14px', borderBottom: '1px solid var(--border)', background: 'var(--surface-alt)' }}>
          <div className="field" style={{ flex: 2, minWidth: 180 }}><label>Item name *</label><input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></div>
          <div className="field" style={{ width: 130 }}><label>Unit</label><input value={draft.unit} onChange={(e) => setDraft({ ...draft, unit: e.target.value })} /></div>
          <div className="field" style={{ width: 90 }}><label>Est. units</label><input type="number" className="tabular mono" value={draft.estUnits} onChange={(e) => setDraft({ ...draft, estUnits: +e.target.value || 0 })} /></div>
          <div className="field" style={{ width: 100 }}><label>Unit ฿</label><input type="number" className="tabular mono" value={draft.unitPrice} onChange={(e) => setDraft({ ...draft, unitPrice: +e.target.value || 0 })} /></div>
          <button className="btn primary sm" disabled={!draft.name.trim()} onClick={() => void add()}><Ico.check /> Save</button>
          <button className="btn ghost sm" onClick={() => { setAdding(false); }}>Cancel</button>
        </div>
      )}
      <div className="card-b flush">
        <table>
          <thead><tr><th>Item</th><th>Unit</th><th className="num">Est. units</th><th className="num">Unit ฿</th><th className="num">Line ฿</th><th>Default</th>{editable && <th></th>}</tr></thead>
          <tbody>
            {shown.map((it: any) => (
              <tr key={it.id}>
                <td style={{ fontWeight: 500 }}>
                  {editable
                    ? <input defaultValue={it.name} onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== it.name) void patch(it.id, { name: v }); }} style={{ width: '100%' }} />
                    : <>{it.name}{it.isFixedMin && <span className="chip" data-tone="slate" style={{ marginLeft: 6 }}><span className="dot" />min cost</span>}</>}
                </td>
                <td style={{ fontSize: 11.5, color: 'var(--text-2)' }}>{editable ? <input defaultValue={it.unit || ''} onBlur={(e) => { if (e.target.value !== (it.unit || '')) void patch(it.id, { unit: e.target.value }); }} style={{ width: 120 }} /> : it.unit}</td>
                <td className="num tabular mono">{it.isFixedMin ? '—' : (editable ? <input type="number" defaultValue={it.estUnits} onBlur={(e) => { if ((+e.target.value || 0) !== it.estUnits) void patch(it.id, { estUnits: +e.target.value || 0 }); }} style={{ width: 64, textAlign: 'right' }} /> : it.estUnits)}</td>
                <td className="num tabular mono">{it.isFixedMin ? '—' : (editable ? <input type="number" defaultValue={it.unitPrice} onBlur={(e) => { if ((+e.target.value || 0) !== it.unitPrice) void patch(it.id, { unitPrice: +e.target.value || 0 }); }} style={{ width: 80, textAlign: 'right' }} /> : it.unitPrice.toLocaleString())}</td>
                <td className="num tabular mono">{dmItemCost(it).toLocaleString()}</td>
                <td>{it.defaultSelected ? <span className="chip" data-tone="green"><span className="dot" />Default</span> : <span style={{ color: 'var(--text-3)', fontSize: 11 }}>optional</span>}</td>
                {editable && <td><button className="btn ghost icon sm" title="Remove from databank" onClick={() => { if (window.confirm(`Remove "${it.name}" from the databank?`)) void removeDmItem(it.id); }}><Ico.trash /></button></td>}
              </tr>
            ))}
            {shown.length === 0 && <tr><td colSpan={editable ? 7 : 6} style={{ padding: 20, textAlign: 'center', color: 'var(--text-2)' }}>No items match.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

